import React, { useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import type { NiceModalHocProps } from "@ebay/nice-modal-react";
import NiceModal from "@ebay/nice-modal-react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Button } from "components/Button";
import { useTranslate } from "hooks";
import { updateSelectedAccessories } from "utils";
import type { Accessory, AccessoryGroup } from "interfaces/schemas/accessories";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { ProductAccessoriesModalPart } from "./ProductAccessoriesModalPart";

interface IProductAccessoriesModalProps extends NiceModalHocProps {
  accessoryGroups: AccessoryGroup[];
  initSelectedAccessories: [] | Accessory[];
}

export const ProductAccessoriesModal: React.FC<IProductAccessoriesModalProps> =
  NiceModal.create(
    Scrivito.connect(({ accessoryGroups, initSelectedAccessories }) => {
      const translate = useTranslate();
      const modal = NiceModal.useModal();
      const { changeProduct } = useProductConfiguratorStore.use.actions();

      const updatedAccessories = useMemo(
        () =>
          updateSelectedAccessories({
            initSelectedAccessories,
            accessoryGroups,
          }),
        [initSelectedAccessories, accessoryGroups],
      );

      const [selectedAccessories, setSelectedAccessories] =
        useState<Accessory[]>(updatedAccessories);

      const handleGroupSelection = (
        groupId: string,
        currentPresentation: string,
      ): Accessory[] | undefined => {
        const group = accessoryGroups.find((g) => g.id === groupId);
        if (!group) {
          return;
        }

        const selectedAccessory = group.accessories.find(
          (accessory) => accessory.currentPresentation === currentPresentation,
        );

        if (selectedAccessory) {
          setSelectedAccessories((prev: Accessory[]) => {
            const filteredAccessories = prev.filter(
              (accessory) => accessory.accessoryGroupId !== groupId,
            );

            return [...filteredAccessories, selectedAccessory];
          });
        }
      };

      const handleUpdateConfiguration = () => {
        const accessoriesToSend = selectedAccessories.filter(
          (accessory) => accessory.seriesId !== 0,
        );

        changeProduct({ newSelectedAccessories: accessoriesToSend });
        modal.remove();
      };

      return (
        <Modal
          isOpen={modal.visible}
          centered={true}
          size="lg"
          toggle={modal.remove}
          scrollable={true}
          style={{ zIndex: 9999 }}
        >
          <ModalHeader
            toggle={modal.remove}
            tag="div"
            className="d-flex align-items-start pb-0"
          >
            <h3 className="text-uppercase lh-32">
              {translate("PRODUCT_CONFIGURATOR_ACCESSORIES")}
            </h3>
          </ModalHeader>
          <ModalBody className="d-flex flex-column gap-4 mb-5">
            {accessoryGroups.map((accessoryGroup) => (
              <ProductAccessoriesModalPart
                accessoryGroup={accessoryGroup}
                selectedAccessories={selectedAccessories}
                key={`group-${accessoryGroup.id}`}
                onSelectionChange={(value) =>
                  handleGroupSelection(accessoryGroup.id, value)
                }
              />
            ))}
          </ModalBody>
          <ModalFooter className="pt-0 d-flex gap-2">
            <Button
              data-testid="product-adding-modal-cancel-btn"
              disabled={false}
              isLoading={false}
              className="m-0 btn-tertiary"
              onClick={modal.remove}
            >
              {translate("CANCEL")}
            </Button>
            <Button
              data-testid="product-adding-modal-save-btn"
              className="m-0"
              onClick={handleUpdateConfiguration}
            >
              {translate("PRODUCT_CONFIGURATOR_UPDATE_CONFIGURATION")}
            </Button>
          </ModalFooter>
        </Modal>
      );
    }),
  );
